export const SIMILARITY_PROMPT = {
  system: `You are a semantic similarity analyzer for multiple-choice questions (MCQs). You will compare MCQs considering:

1. Question text similarity (40% weight)
   - Core concept alignment
   - Wording and phrasing similarity
   - Context and scenario similarity

2. Answer options similarity (40% weight)
   - Option content similarity
   - Option structure and format
   - Distractors similarity pattern

3. Correct answer alignment (20% weight)
   - Whether the correct answers test the same knowledge point
   - Similarity in reasoning required

Return a precise similarity score between 0 and 1 where:
- 0.95-1.00: Nearly identical questions (same concept, very similar wording)
- 0.90-0.94: Extremely similar questions (same concept, different wording)
- 0.85-0.89: Very similar questions (same concept, different approach)
- 0.80-0.84: Similar questions (same concept, significantly different approach)
- 0.75-0.79: Moderately similar questions (related concepts, similar approach)
- 0.70-0.74: Somewhat similar questions (related concepts, different approach)
- Below 0.70: Different questions

CRITICAL: 
- Return ONLY a JSON array of numbers with exactly one score per comparison MCQ
- Use precise decimal scores (e.g., 0.87, 0.92) rather than rounded values
- Do not include any additional text or explanations
- Consider the weighted criteria carefully when calculating scores`,

  generateUserPrompt: (sourceQuestion: string, questionsToCompare: string[]): string => {
    return `Compare this source MCQ with the following ${questionsToCompare.length} MCQs and return a JSON array of precise similarity scores (one score per MCQ).

Scoring Criteria:
1. Question Text (40%): Analyze core concept alignment, wording similarity, and context
2. Answer Options (40%): Compare option content, structure, and distractor patterns
3. Correct Answer (20%): Evaluate knowledge point and reasoning alignment

Source MCQ:
${sourceQuestion}

MCQs to compare:
${questionsToCompare.map((q, i) => `${i + 1}. ${q}`).join('\n\n')}

CRITICAL: 
- Return ONLY the array of scores as a valid JSON array
- Use precise decimal scores (e.g., [0.87, 0.92, 0.76])
- No rounding to quarters or halves
- No additional text or explanations`;
  },

  parseScores: (content: string, expectedCount: number): number[] => {
    try {
      // Clean the content: remove any non-JSON text and whitespace
      const cleanContent = content.replace(/^[\s\S]*?\[/, '[').replace(/\][\s\S]*$/, ']').trim();
      
      if (!cleanContent.startsWith('[') || !cleanContent.endsWith(']')) {
        throw new Error('Response is not a valid JSON array');
      }

      // Parse the JSON array
      const scores = JSON.parse(cleanContent);

      if (!Array.isArray(scores)) {
        throw new Error('Parsed content is not an array');
      }

      // Validate and normalize each score with high precision
      const validatedScores = scores.map((score, index) => {
        // Handle both string and number inputs
        const numScore = typeof score === 'string' ? parseFloat(score) : score;
        
        // Validate the score
        if (typeof numScore !== 'number' || isNaN(numScore)) {
          console.warn(`Invalid score at index ${index}, defaulting to 0`);
          return 0;
        }
        
        // Clamp the score between 0 and 1 with high precision
        return Number(Math.max(0, Math.min(1, numScore)).toFixed(4));
      });

      // Handle score count mismatch
      if (validatedScores.length !== expectedCount) {
        console.warn(`Score count mismatch. Expected: ${expectedCount}, Got: ${validatedScores.length}`);
        
        if (validatedScores.length > expectedCount) {
          validatedScores.length = expectedCount;
        } else {
          while (validatedScores.length < expectedCount) {
            validatedScores.push(0);
          }
        }
      }

      return validatedScores;
    } catch (error) {
      console.error('Score parsing error:', error);
      console.error('Original content:', content);
      console.error('Expected count:', expectedCount);
      
      // Return array of zeros as fallback
      return new Array(expectedCount).fill(0);
    }
  }
};